'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { TABLET_BREAKPOINT } from '@/configs/layout';

interface Result {
  configMenuOpen: boolean;
  isMobile: boolean;
  sidebarExpanded: boolean;
  setConfigMenuOpen: (open: boolean) => void;
  setSidebarExpanded: (expanded: boolean) => void;
}

const CONFIG_MENU_OPEN = 'configMenuOpen';
const SIDEBAR_EXPANDED = 'sidebarExpanded';

function getDefaultResult() {
  return {
    configMenuOpen: getDefaultConfigMenuOpen(),
    isMobile: false,
    sidebarExpanded: getDefaultSidebarExpanded(),
    setConfigMenuOpen: () => {},
    setSidebarExpanded: () => {},
  };
}

function getDefaultConfigMenuOpen() {
  if (typeof window === 'undefined') return true;

  const sidebarExpanded = getDefaultSidebarExpanded();
  const stored = window.localStorage.getItem(CONFIG_MENU_OPEN);
  if (stored === null) return sidebarExpanded;
  return (
    sidebarExpanded && window.localStorage.getItem(CONFIG_MENU_OPEN) === 'true'
  );
}

function getDefaultSidebarExpanded() {
  if (typeof window === 'undefined') return true;

  const stored = window.localStorage.getItem(SIDEBAR_EXPANDED);
  if (stored === null) return true;
  return stored === 'true';
}

const ShellContext = createContext<Result>(getDefaultResult());

export function useShell() {
  return useContext(ShellContext);
}

export function ShellContextProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = (event: MediaQueryListEvent) => {
      setContextData((prev) => {
        if (prev.isMobile !== event.matches) {
          return { ...prev, isMobile: event.matches };
        }
        return prev;
      });
    };

    const mql = window.matchMedia(`(max-width: ${TABLET_BREAKPOINT}px)`);
    mql.addEventListener('change', handleResize);

    setContextData((prev) => {
      if (prev.isMobile !== mql.matches) {
        return { ...prev, isMobile: mql.matches };
      }
      return prev;
    });

    return () => {
      mql.removeEventListener('change', handleResize);
    };
  }, []);

  const setConfigMenuOpen = useCallback((value: boolean) => {
    setContextData((prev) => {
      if (prev.configMenuOpen === value) {
        return prev;
      }
      localStorage.setItem(CONFIG_MENU_OPEN, String(value));
      return { ...prev, configMenuOpen: value };
    });
  }, []);

  const setSidebarExpanded = useCallback((value: boolean) => {
    setContextData((prev) => {
      if (prev.sidebarExpanded === value) {
        return prev;
      }
      localStorage.setItem(SIDEBAR_EXPANDED, String(value));
      return { ...prev, sidebarExpanded: value };
    });
  }, []);

  const [contextData, setContextData] = useState<Result>(() => {
    return {
      ...getDefaultResult(),
      setConfigMenuOpen,
      setSidebarExpanded,
    };
  });

  return (
    <ShellContext.Provider value={contextData}>
      {children}
    </ShellContext.Provider>
  );
}
